import React, { memo } from 'react';
interface TellCardProps {
  number: string;
  gaugeValue: number;
  gaugeColor: string;
  title: string;
  subtitle: string;
  description: string;
}
function TellCard({
  number,
  gaugeValue,
  gaugeColor,
  title,
  subtitle,
  description
}: TellCardProps) {
  const radius = 28;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - gaugeValue / 100 * circumference;
  const getStrokeColor = (color: string) => {
    switch (color) {
      case 'green':
        return '#22c55e';
      case 'orange':
        return '#f59e0b';
      case 'red':
        return '#ef4444';
      default:
        return '#3b82f6';
    }
  };
  return <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 flex flex-col h-full" data-id="element-166">
      <div className="flex justify-between items-start mb-8" data-id="element-167">
        <span className="text-gray-500 text-sm font-mono" data-id="element-168">{number}</span>
        <div className="relative w-16 h-16 flex items-center justify-center" data-id="element-169">
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 64 64" data-id="element-170">
            <circle cx="32" cy="32" r={radius} stroke="#1f2937" strokeWidth="4" fill="transparent" data-id="element-171" />
            <circle cx="32" cy="32" r={radius} stroke={getStrokeColor(gaugeColor)} strokeWidth="4" fill="transparent" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} strokeLinecap="round" data-id="element-172" />
          </svg>
          <span className="absolute text-sm font-bold text-white" data-id="element-173">
            {gaugeValue}
          </span>
        </div>
      </div>

      <h3 className="text-white font-bold text-xl tracking-wider uppercase mb-2 font-['Syne']" data-id="element-174">
        {title}
      </h3>
      <p className="text-gray-400 text-sm uppercase tracking-wide mb-5" data-id="element-175">
        {subtitle}
      </p>
      <p className="text-gray-300 text-base leading-relaxed" data-id="element-176">{description}</p>
    </div>;
}
export function FourTellsSection() {
  return <section className="py-20 px-6 md:px-12 lg:px-24 bg-white border-t border-gray-100" data-id="element-177">
      <div className="max-w-7xl mx-auto" data-id="element-178">
        <div className="mb-16" data-id="element-179">
          <span className="text-sm font-bold tracking-widest uppercase text-[#0066FF] mb-4 block font-sans" data-id="element-180">
            The Drift Index
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 font-['Syne'] leading-tight" data-id="element-181">
            Every agent drifts.
            <br data-id="element-182" />
            Here are the four fidelity signals.
          </h2>
          <p className="text-xl text-gray-600 font-light max-w-3xl" data-id="element-183">
            An agent can have perfect uptime, low latency, and zero errors —
            while slowly becoming unfaithful to its purpose. Current
            observability monitors the machine. Synthia monitors the mission.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-184">
          <TellCard number="01" gaugeValue={82} gaugeColor="orange" title="INFERENCE CREEP" subtitle="Unsolicited additions not present in input" description="The agent starts adding connections, assumptions, and context that nobody asked for. Early on, it looks like helpfulness. Over time, it compounds into confabulation. You compare input to output, and there's information manufactured in the gap." data-id="element-185" />
          <TellCard number="02" gaugeValue={94} gaugeColor="green" title="SPECIFICITY" subtitle="Precision of outputs vs. inputs" description='Specific questions start producing generic answers. The outputs get softer, wider, less precise. Ask for "Q3 revenue in enterprise" and you get "strong growth across segments." The agent isn&apos;t wrong. It&apos;s just vague. And getting vaguer.' data-id="element-186" />
          <TellCard number="03" gaugeValue={97} gaugeColor="green" title="CONTEXT BLEED" subtitle="Cross-task information leakage" description="Information from Task A starts appearing in Task B's outputs. In single-agent systems, this means cross-session contamination. In multi-agent systems, it means one agent's context is leaking into another's reasoning — a compliance nightmare." data-id="element-187" />
          <TellCard number="04" gaugeValue={67} gaugeColor="red" title="GROUNDING RATIO" subtitle="Output traceable to actual input" description="How much of the agent's output is traceable to what it was actually given versus what it pulled from parametric memory? As grounding drops, the agent substitutes your institutional knowledge with its own — making it look competent while ignoring your rules." data-id="element-188" />
        </div>
      </div>
    </section>;
}